import type { BarChartSeries } from "@mantine/charts";
import { Card, Group, Space, Text, Title } from "@mantine/core";
import { numericFormatter } from "react-number-format";
import { NoChartDataWrapper } from "../../../../mantine/charts/components/NoChartDataWrapper/NoChartDataWrapper";
import { MantineBarChart } from "../../../Charts/BarChart/MantineBarChart";
import { MantineBarChartTooltip } from "../../../Charts/BarChart/MantineBarChartTooltip";
import type { EnergyRevenuesListItem } from "../../RevenueMonitoring.types";
import { TotalsSideBar } from "../../TotalsSideBar/TotalsSideBar";
import { REVENUE_TABLE_HEADER_TEXTS } from "../EnergyRevenuesTable/EnergyRevenuesTable";

const UNIT = " €";

const SERIES = [
  {
    label: REVENUE_TABLE_HEADER_TEXTS.net_revenue_minus_fee,
    name: "net_revenue_minus_fee",
    color: "#118EB0",
    stackId: "a"
  },
  {
    label: REVENUE_TABLE_HEADER_TEXTS.market_premium_revenue,
    name: "market_premium_revenue",
    color: "#42B8B2",
    stackId: "a"
  },
  {
    label: REVENUE_TABLE_HEADER_TEXTS.negative_price_revenue,
    name: "negative_price_revenue",
    color: "#4D4C7E",
    stackId: "b"
  }
] as BarChartSeries[];

const INACTIVE_SERIES = ["negative_price_revenue"];

interface RevenueOverviewChartProps {
  formattedData: Array<
    Pick<
      EnergyRevenuesListItem,
      | "net_revenue_minus_fee"
      | "market_premium_revenue"
      | "negative_price_revenue"
      | "period_start"
    >
  >;
  isLoading?: boolean;
}

function RevenueOverviewChart({
  formattedData,
  isLoading = false
}: RevenueOverviewChartProps) {
  const sums = formattedData.reduce(
    (acc, item) => ({
      net_revenue_minus_fee:
        acc.net_revenue_minus_fee + (item.net_revenue_minus_fee ?? 0),
      market_premium_revenue:
        acc.market_premium_revenue + (item.market_premium_revenue ?? 0),
      negative_price_revenue:
        acc.negative_price_revenue + (item.negative_price_revenue ?? 0)
    }),
    {
      net_revenue_minus_fee: 0,
      market_premium_revenue: 0,
      negative_price_revenue: 0
    }
  );

  const totals = {
    net_revenue_minus_fee: formattedData.some(
      (item) => item.net_revenue_minus_fee !== null
    )
      ? sums.net_revenue_minus_fee
      : null,
    market_premium_revenue: formattedData.some(
      (item) => item.market_premium_revenue !== null
    )
      ? sums.market_premium_revenue
      : null,
    negative_price_revenue: formattedData.some(
      (item) => item.negative_price_revenue !== null
    )
      ? sums.negative_price_revenue
      : null
  };

  return (
    <Card w="100%" withBorder>
      <Title order={6}>Erlösübersicht in EUR</Title>
      <Text>
        Hier sehen Sie die Erlöse im ausgewählten Zeitraum, aufgeteilt nach
        Erlösen aus der Vermarktung abzgl. des Vermarktungsentgelts und der
        Marktprämie. Alle Angaben ohne MwSt..
      </Text>
      <Space h="lg" />
      <Group align="center" wrap="nowrap">
        <NoChartDataWrapper
          isLoading={isLoading}
          noData={formattedData.length === 0}
        >
          <MantineBarChart
            data={formattedData}
            dataKey="period_start"
            defaultInactiveSeries={INACTIVE_SERIES}
            legendPosition="top"
            series={SERIES}
            tooltipProps={{
              content: ({ label, payload }) => {
                if (!payload?.length) return null;
                return (
                  <MantineBarChartTooltip
                    payload={[
                      {
                        name: "total_revenue",
                        color: "#F6B93B",
                        value:
                          (payload?.[0]?.payload?.net_revenue_minus_fee ?? 0) +
                          (payload?.[0]?.payload?.market_premium_revenue ?? 0),
                        payload: {
                          category: `Gesamterlös`
                        },
                        graphicalItemId: "total_revenue"
                      },
                      ...payload
                    ]}
                    seriesLabels={SERIES.reduce(
                      (result, singleSeries) => ({
                        ...result,
                        [singleSeries.name]:
                          singleSeries.label || singleSeries.name
                      }),
                      {}
                    )}
                    title={String(label)}
                    valueFormatter={(value) =>
                      numericFormatter(value.toString(), {
                        decimalScale: 2,
                        decimalSeparator: ",",
                        thousandSeparator: "."
                      })
                    }
                  />
                );
              }
            }}
            unit={UNIT}
            withLegend
            xAxisProps={{
              interval: 1
            }}
            yAxisProps={{
              tickFormatter: (value) =>
                numericFormatter((value / 1000).toString(), {
                  decimalScale: 2,
                  decimalSeparator: ",",
                  suffix: "k",
                  thousandSeparator: ".",
                  fixedDecimalScale: true
                })
            }}
          />
        </NoChartDataWrapper>
        <TotalsSideBar
          totalsSeries={[
            {
              label: "Gesamterlös",
              value:
                totals.net_revenue_minus_fee !== null &&
                totals.market_premium_revenue !== null
                  ? totals.net_revenue_minus_fee + totals.market_premium_revenue
                  : null,
              color: "#F6B93B"
            },
            ...SERIES.map((series) => ({
              label: series.label,
              value: totals[series.name],
              color: series.color
            }))
          ]}
          unit={UNIT}
        />
      </Group>
    </Card>
  );
}

export { RevenueOverviewChart, RevenueOverviewChartProps };
